import clsx from "clsx";
import { ConditionalTooltip } from "components/ConditionalTooltip";
import { IconText } from "components/IconText";
import { ModalButton } from "components/modals/ModalButton";
import { ModalDialog } from "components/modals/ModalDialog";
import { CardPlaceholder } from "components/placeholders/CardPlaceholder";
import { GlobalContext } from "contexts/GlobalContext";
import { ReactNode, useContext, useState } from "react";
import { Link } from "react-router-dom";
import { GetCreateGameResult } from "results/frankenstories/GetCreateGameResult";
import { RouteList } from "Routes";

const CreateOption = (props: { icon: string; title: string; description: string; to: string; disabled?: boolean }) => {
    const { description, icon, title, to, disabled } = props;

    return (
        <ConditionalTooltip show={!!disabled} text="Subscribe to Frankenstories Pro to use the prompt library">
            <div
                className={clsx(
                    "bg-dark text-white p-3 rounded text-center position-relative",
                    disabled && "opacity-50",
                )}
            >
                <h3 className="h5">{title}</h3>
                <div className="fs-1">{icon}</div>
                <div>{description}</div>
                {!disabled && <Link to={to} className="stretched-link stretched-link-hover"></Link>}
            </div>
        </ConditionalTooltip>
    );
};

export const CreateGameModalButton = (props: { className: string; text?: ReactNode }) => {
    const { className, text = <IconText icon="fas fa-plus" text="Create" /> } = props;

    const { userData, isAuthenticated } = useContext(GlobalContext);

    if (userData?.isStudent || !isAuthenticated) {
        return (
            <Link to={RouteList.GamesNewGame} className={className}>
                {text}
            </Link>
        );
    }

    return <ModalButton id="modal-create-game" className={className} text={text} />;
};

export const CreateGameModal = () => {
    const { gamesService, catchServerError, globalData } = useContext(GlobalContext);
    const { getCreateGame } = gamesService;

    const [data, setData] = useState<GetCreateGameResult>();

    const hasTemplates = !!data?.hasTemplates;
    const columnClass = hasTemplates ? "g-col-12 g-col-lg-4" : "g-col-12 g-col-lg-6";

    return (
        <ModalDialog
            id="modal-create-game"
            title="Where to begin?"
            size={hasTemplates ? "lg" : "md"}
            onOpen={() => {
                getCreateGame()
                    .then(x => {
                        setData(x);
                    })
                    .catch(catchServerError);
            }}
        >
            {data == null ? (
                <div className="grid">
                    <div className="g-col-12 g-col-lg-4">
                        <CardPlaceholder />
                    </div>

                    <div className="g-col-12 g-col-lg-4">
                        <CardPlaceholder />
                    </div>

                    <div className="g-col-12 g-col-lg-4">
                        <CardPlaceholder />
                    </div>
                </div>
            ) : (
                <div className="grid">
                    <div className={columnClass}>
                        <CreateOption
                            title="Discover"
                            description="Visit prompt library"
                            icon="🧭"
                            to={RouteList.Discover}
                            disabled={!globalData?.isSubscribed}
                        />
                    </div>

                    <div className={columnClass}>
                        <CreateOption title="New" description="Create a prompt" icon="💡" to={RouteList.GamesNewGame} />
                    </div>

                    {data.hasTemplates && (
                        <div className={columnClass}>
                            <CreateOption
                                title="Templates"
                                description="Use previous game"
                                icon="📚"
                                to={RouteList.GamesTemplates}
                            />
                        </div>
                    )}
                </div>
            )}
        </ModalDialog>
    );
};
